import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  ApiError,
  apiFetch,
  loadStoredToken,
  setAccessToken,
  setUnauthorizedHandler,
} from "@/api/client";
import type { TokenResponse, User, UserRole } from "@/api/types";
import { AuthContext, type AuthState } from "./auth-context";

/** The last identity the server confirmed, so the app can open without network. */
const USER_STORAGE_KEY = "hdv.user";

function rememberUser(user: User | null): void {
  try {
    if (user) window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    else window.localStorage.removeItem(USER_STORAGE_KEY);
  } catch {
    /* private browsing: the app simply asks the server again next time */
  }
}

function recallUser(): User | null {
  try {
    const raw = window.localStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");

  const logout = useCallback(() => {
    setAccessToken(null);
    rememberUser(null);
    setUser(null);
    setStatus("anonymous");
    queryClient.clear();
  }, [queryClient]);

  // A 401 anywhere in the app means the session is over; the client tells us.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      rememberUser(null);
      setUser(null);
      setStatus("anonymous");
      queryClient.clear();
    });
    return () => setUnauthorizedHandler(null);
  }, [queryClient]);

  // Restore a session from a stored token, if the server still accepts it.
  useEffect(() => {
    let cancelled = false;
    const token = loadStoredToken();
    if (!token) {
      setStatus("anonymous");
      return;
    }
    // Open with the last confirmed identity, then check it. Without this the
    // app cannot start at all without network, which would make the offline
    // shell pointless — and the queue unsendable, since the token would be gone.
    const remembered = recallUser();
    if (remembered) {
      setUser(remembered);
      setStatus("authenticated");
    }

    apiFetch<User>("/auth/me")
      .then((me) => {
        if (cancelled) return;
        rememberUser(me);
        setUser(me);
        setStatus("authenticated");
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        // A network failure is not an expired session. Throwing the token away
        // here means going into a basement logs you out, permanently, along
        // with everything waiting in the queue to be sent.
        const offline = caught instanceof ApiError && caught.status === 0;
        if (offline && remembered) return;
        if (offline) {
          setStatus("anonymous");
          return;
        }
        setAccessToken(null);
        rememberUser(null);
        setStatus("anonymous");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const token = await apiFetch<TokenResponse>("/auth/login", {
        method: "POST",
        body: { email, password },
        anonymous: true,
      });
      setAccessToken(token.access_token);
      const me = await apiFetch<User>("/auth/me");
      rememberUser(me);
      setUser(me);
      setStatus("authenticated");
    },
    [],
  );

  const can = useCallback(
    (...roles: UserRole[]) => (user ? roles.includes(user.role) : false),
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({ user, status, login, logout, can }),
    [user, status, login, logout, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
